
import { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { SortField, SortOrder, ViewMode } from "@/components/candidates/CandidatesViewOptions";
import { QueryDefinition } from "@/types/savedQuery";

interface CandidatesFilterContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  skillsFilter: string[];
  setSkillsFilter: (skills: string[]) => void;
  skillsOperator: "AND" | "OR";
  setSkillsOperator: (operator: "AND" | "OR") => void;
  statusFilter: string[];
  setStatusFilter: (statuses: string[]) => void;
  functionFilter: string;
  setFunctionFilter: (functionName: string) => void;
  assignedToMe: boolean;
  setAssignedToMe: (assigned: boolean) => void;
  showFavoritesOnly: boolean;
  setShowFavoritesOnly: (show: boolean) => void;
  sortField: SortField;
  setSortField: (field: SortField) => void;
  sortOrder: SortOrder;
  setSortOrder: (order: SortOrder) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  activeQueryId: string | null;
  setActiveQueryId: (id: string | null) => void;
  clearAllFilters: () => void;
  getCurrentFiltersAsQueryDefinition: () => QueryDefinition;
  applyQueryDefinition: (definition: QueryDefinition, queryId?: string) => void;
}

interface CandidatesFilterProviderProps {
  children: ReactNode;
  viewMode?: ViewMode;
  setViewMode?: (mode: ViewMode) => void;
}

const CandidatesFilterContext = createContext<CandidatesFilterContextType | undefined>(undefined);

export function CandidatesFilterProvider({ 
  children,
  viewMode: externalViewMode,
  setViewMode: externalSetViewMode
}: CandidatesFilterProviderProps) {
	const [searchParams, setSearchParams] = useSearchParams();
	
	// Initialize state from URL parameters
	const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
	const [skillsFilter, setSkillsFilter] = useState<string[]>(() => {
		const skills = searchParams.get("skills");
		return skills ? skills.split(",") : [];
	});
	const [skillsOperator, setSkillsOperator] = useState<"AND" | "OR">(() => {
		const operator = searchParams.get("skillsOp");
		return operator === "OR" ? "OR" : "AND";
	});
	const [statusFilter, setStatusFilter] = useState<string[]>(() => {
		const status = searchParams.get("status");
		return status ? status.split(",") : [];
	});
	const [functionFilter, setFunctionFilter] = useState(() => searchParams.get("function") || "");
	const [assignedToMe, setAssignedToMe] = useState<boolean>(() => searchParams.get("assignedToMe") === "true");
	const [showFavoritesOnly, setShowFavoritesOnly] = useState<boolean>(() => searchParams.get("favoritesOnly") === "true");
	const [sortField, setSortField] = useState<SortField>(() => {
		const field = searchParams.get("sortField");
		return (field as SortField) || "name";
	});
	const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
		const order = searchParams.get("sortOrder");
		return (order as SortOrder) || "asc";
	});
	const [internalViewMode, setInternalViewMode] = useState<ViewMode>("card");
	const [currentPage, setCurrentPage] = useState(() => {
		const page = searchParams.get("page");
		return page ? parseInt(page, 10) : 1;
	});
	const [activeQueryId, setActiveQueryId] = useState<string | null>(null);

	// Use either external view mode state or internal
	const viewMode = externalViewMode !== undefined ? externalViewMode : internalViewMode;
	const setViewMode = externalSetViewMode || setInternalViewMode;

	// Track last URL params we set to avoid unnecessary updates
	const lastParamsRef = useRef<string>("");
	const isInitialMount = useRef(true);

	// Update URL parameters whenever filters change
	useEffect(() => {
		// Skip URL sync on initial mount (params are already in URL)
		if (isInitialMount.current) {
			isInitialMount.current = false;
			lastParamsRef.current = searchParams.toString();
			return;
		}

		const params = new URLSearchParams();
		
		if (searchTerm)
			params.set("search", searchTerm);
		if (skillsFilter.length > 0)
			params.set("skills", skillsFilter.join(","));
		if (skillsOperator !== "AND")
			params.set("skillsOp", skillsOperator);
		if (statusFilter.length > 0)
			params.set("status", statusFilter.join(","));
		if (functionFilter)
			params.set("function", functionFilter);
		if (assignedToMe)
			params.set("assignedToMe", "true");
		if (showFavoritesOnly)
			params.set("favoritesOnly", "true");
		if (sortField !== "name")
			params.set("sortField", sortField);
		if (sortOrder !== "asc")
			params.set("sortOrder", sortOrder);
		if (currentPage !== 1)
			params.set("page", currentPage.toString());
		
		// Only update URL if params are different from last time we set them
		const newParamsString = params.toString();
		if (newParamsString !== lastParamsRef.current) {
			lastParamsRef.current = newParamsString;
			setSearchParams(params, { replace: true });
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchTerm, skillsFilter, skillsOperator, statusFilter, functionFilter, assignedToMe, showFavoritesOnly, sortField, sortOrder, currentPage]);

	const clearAllFilters = () => {
		setSearchTerm("");
		setSkillsFilter([]);
		setSkillsOperator("AND");
		setStatusFilter([]);
		setFunctionFilter("");
		setAssignedToMe(false); // Reset to show all candidates
		setShowFavoritesOnly(false);
		setCurrentPage(1);
		setActiveQueryId(null); // Clear active query
	};

	const getCurrentFiltersAsQueryDefinition = useCallback((): QueryDefinition => ({
		searchTerm,
		skillsFilter,
		skillsOperator,
		statusFilter,
		functionFilter,
		assignedToMe,
		showFavoritesOnly,
		sortField,
		sortOrder,
	}), [searchTerm, skillsFilter, skillsOperator, statusFilter, functionFilter, assignedToMe, showFavoritesOnly, sortField, sortOrder]);

	const applyQueryDefinition = useCallback((definition: QueryDefinition, queryId?: string) => {
		if (definition.searchTerm !== undefined) setSearchTerm(definition.searchTerm);
		if (definition.skillsFilter !== undefined) setSkillsFilter(definition.skillsFilter);
		if (definition.skillsOperator !== undefined) setSkillsOperator(definition.skillsOperator);
		if (definition.statusFilter !== undefined) setStatusFilter(definition.statusFilter);
		if (definition.functionFilter !== undefined) setFunctionFilter(definition.functionFilter);
		if (definition.assignedToMe !== undefined) setAssignedToMe(definition.assignedToMe);
		if (definition.showFavoritesOnly !== undefined) setShowFavoritesOnly(definition.showFavoritesOnly);
		if (definition.sortField !== undefined) setSortField(definition.sortField);
		if (definition.sortOrder !== undefined) setSortOrder(definition.sortOrder);
		setCurrentPage(1); // Reset to first page when applying a saved query
		setActiveQueryId(queryId || null); // Track which query is active
	}, []);

  return (
    <CandidatesFilterContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        skillsFilter,
        setSkillsFilter,
        skillsOperator,
        setSkillsOperator,
        statusFilter,
        setStatusFilter,
        functionFilter,
        setFunctionFilter,
        assignedToMe,
        setAssignedToMe,
        showFavoritesOnly,
        setShowFavoritesOnly,
        sortField,
        setSortField,
        sortOrder,
        setSortOrder,
        viewMode,
        setViewMode,
        currentPage,
        setCurrentPage,
        activeQueryId,
        setActiveQueryId,
        clearAllFilters,
        getCurrentFiltersAsQueryDefinition,
        applyQueryDefinition,
      }}
    >
      {children}
    </CandidatesFilterContext.Provider>
  );
}

export const useCandidatesFilter = () => {
  const context = useContext(CandidatesFilterContext);
  if (context === undefined) {
    throw new Error("useCandidatesFilter must be used within a CandidatesFilterProvider");
  }
  return context;
};
